import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { TextInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import {
  getDatevSettings,
  runDatevBackfill,
  runSharepointBackfill,
  saveDatevSettings,
} from "@/lib/dokumente";
import { getInvoiceDatevSettings, saveInvoiceDatevSettings } from "@/lib/invoiceApproval";
import { toast } from "@/lib/toast";
import { SettingsAccessDenied } from "../../SettingsLayout";
import { useSettingsAcl } from "../../lib/useSettingsAcl";

function SenderAddressField({ address }: { address: string }) {
  const shown = address.trim();

  async function copy() {
    if (!shown) return;
    try {
      await navigator.clipboard.writeText(shown);
      toast.success("Absenderadresse kopiert.");
    } catch {
      toast.error("Kopieren nicht möglich. Bitte die Adresse markieren.");
    }
  }

  return (
    <div className="space-y-2 rounded-lg border border-separator/60 bg-fill-tertiary/40 p-3">
      <TextInput
        label="Absenderadresse"
        value={shown || "nicht konfiguriert"}
        readOnly
        onFocus={(event) => event.currentTarget.select()}
      />
      <p className="text-xs text-label-secondary">
        Diese Adresse in DATEV als zugelassenen Absender freigeben. Der Versand läuft über diese
        feste Adresse, nicht über ein persönliches Postfach.
      </p>
      <Button type="button" variant="secondary" size="sm" onClick={() => void copy()} disabled={!shown}>
        Adresse kopieren
      </Button>
    </div>
  );
}

function formatDateDe(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString("de-DE");
}

export function DatevSettingsSection() {
  const acl = useSettingsAcl();
  const [email, setEmail] = useState("");
  const [savedEmail, setSavedEmail] = useState("");
  const [fromAddress, setFromAddress] = useState("");
  const [incomingEmail, setIncomingEmail] = useState("");
  const [savingIncoming, setSavingIncoming] = useState(false);
  const [backfillFrom, setBackfillFrom] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [datevBackfilling, setDatevBackfilling] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [incomingError, setIncomingError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setIncomingError(null);
    const [outgoingResult, incomingResult] = await Promise.allSettled([
      getDatevSettings(),
      getInvoiceDatevSettings(),
    ]);
    if (outgoingResult.status === "fulfilled") {
      setEmail(outgoingResult.value.email);
      setSavedEmail(outgoingResult.value.email);
      if (outgoingResult.value.from_address) setFromAddress(outgoingResult.value.from_address);
    } else {
      const err = outgoingResult.reason;
      setError(err instanceof ApiError ? err.message : "DATEV-Einstellungen konnten nicht geladen werden.");
    }
    if (incomingResult.status === "fulfilled") {
      setIncomingEmail(incomingResult.value.email);
      if (incomingResult.value.from_address) setFromAddress(incomingResult.value.from_address);
    } else {
      const err = incomingResult.reason;
      setIncomingError(
        err instanceof ApiError ? err.message : "DATEV-Einstellungen für Eingangsrechnungen konnten nicht geladen werden.",
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!acl.loading && !acl.canAuftraege) {
    return <SettingsAccessDenied />;
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const saved = await saveDatevSettings(email.trim());
      setEmail(saved.email);
      setSavedEmail(saved.email);
      if (saved.from_address) setFromAddress(saved.from_address);
      toast.success(
        saved.email
          ? "DATEV-E-Mail-Adresse gespeichert."
          : "DATEV-E-Mail-Adresse geleert – automatischer Versand ist aus.",
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDatevBackfill() {
    if (!savedEmail.trim()) {
      setError("Bitte zuerst eine DATEV-E-Mail-Adresse speichern.");
      return;
    }
    if (!backfillFrom) {
      setError("Bitte ein Belegdatum für den Nachzug wählen.");
      return;
    }
    if (
      !window.confirm(
        `Alle noch nicht an DATEV gesendeten Rechnungen ab Belegdatum ${formatDateDe(backfillFrom)} jetzt nachziehen?`,
      )
    ) {
      return;
    }
    setDatevBackfilling(true);
    setError(null);
    try {
      const result = await runDatevBackfill({ fromDate: backfillFrom });
      toast.success(
        `DATEV-Nachzug: ${result.sent} gesendet, ${result.skipped} übersprungen, ${result.failed} fehlgeschlagen (${result.total} geprüft).`,
      );
      const firstError = result.errors?.[0];
      if (result.failed > 0 && firstError) {
        setError(
          `Einige DATEV-Mails sind fehlgeschlagen (z. B. Dokument #${firstError.doc_id}: ${firstError.error}).`,
        );
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "DATEV-Nachzug fehlgeschlagen.");
    } finally {
      setDatevBackfilling(false);
    }
  }

  async function handleBackfill() {
    if (
      !window.confirm(
        "Alle erstellten Geschäftsdokumente ohne SharePoint-Eintrag als PDF in den jeweiligen Projektordner legen?",
      )
    ) {
      return;
    }
    setBackfilling(true);
    setError(null);
    try {
      const result = await runSharepointBackfill({ force: false });
      toast.success(
        `SharePoint-Nachzug: ${result.uploaded} hochgeladen, ${result.skipped} übersprungen, ${result.failed} fehlgeschlagen (${result.total} geprüft).`,
      );
      const firstError = result.errors?.[0];
      if (result.failed > 0 && firstError) {
        setError(
          `Einige Uploads sind fehlgeschlagen (z. B. Dokument #${firstError.doc_id}: ${firstError.error}).`,
        );
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "SharePoint-Nachzug fehlgeschlagen.");
    } finally {
      setBackfilling(false);
    }
  }

  async function handleSaveIncoming() {
    setSavingIncoming(true);
    setIncomingError(null);
    try {
      const saved = await saveInvoiceDatevSettings(incomingEmail.trim());
      setIncomingEmail(saved.email);
      if (saved.from_address) setFromAddress(saved.from_address);
      toast.success(
        saved.email
          ? "DATEV-Adresse für Eingangsrechnungen gespeichert."
          : "DATEV-Adresse geleert – freigegebene Eingangsrechnungen werden nicht versendet.",
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSavingIncoming(false);
    }
  }

  const canDatevBackfill = Boolean(savedEmail.trim()) && Boolean(backfillFrom);

  return (
    <div className="space-y-4">
      <Card
        title="DATEV – Ausgangsrechnungen"
        description="Hinterlegte Adresse erhält automatisch das Rechnungs-PDF (inkl. ZUGFeRD), sobald eine Rechnung den Status „erstellt“ erhält."
        actions={
          <Button type="button" onClick={() => void handleSave()} disabled={saving || loading}>
            Speichern
          </Button>
        }
      >
        {error ? <Alert variant="error">{error}</Alert> : null}
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-label-secondary">
            <Spinner /> Laden …
          </div>
        ) : (
          <div className="max-w-xl space-y-4">
            <div className="space-y-2">
              <TextInput
                label="DATEV-E-Mail-Adresse"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="datev@beispiel.de"
                autoComplete="off"
              />
              <p className="text-xs text-label-secondary">
                Leer lassen, um den automatischen DATEV-Versand zu deaktivieren. Der Versand
                unterliegt dem Safety-Mode.
              </p>
              <SenderAddressField address={fromAddress} />
            </div>

            <div className="space-y-2 border-t border-separator/40 pt-4">
              <p className="text-sm font-medium text-label">Vergangene Rechnungen nachziehen</p>
              <p className="text-xs text-label-secondary">
                Sendet alle noch nicht an DATEV übermittelten Rechnungen ab dem gewählten
                Belegdatum (ohne Entwürfe und Stornos).
              </p>
              <TextInput
                label="Nachzug ab Belegdatum"
                type="date"
                value={backfillFrom}
                onChange={(e) => setBackfillFrom(e.target.value)}
                disabled={!savedEmail.trim() || datevBackfilling}
              />
              {!savedEmail.trim() ? (
                <p className="text-xs text-label-secondary">
                  Speichern Sie zuerst eine DATEV-E-Mail-Adresse, um den Nachzug zu starten.
                </p>
              ) : null}
              <Button
                type="button"
                variant="secondary"
                onClick={() => void handleDatevBackfill()}
                disabled={!canDatevBackfill || datevBackfilling || loading}
              >
                {datevBackfilling ? "Nachzug läuft …" : "Nachzug starten"}
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card
        title="DATEV – Eingangsrechnungen"
        description="Hinterlegte Adresse erhält die Rechnung, sobald alle zugewiesenen Personen freigegeben haben."
        actions={
          <Button type="button" onClick={() => void handleSaveIncoming()} disabled={savingIncoming || loading}>
            Speichern
          </Button>
        }
      >
        {incomingError ? <Alert variant="error">{incomingError}</Alert> : null}
        {loading ? null : (
          <div className="max-w-xl space-y-4">
            <div className="space-y-2">
              <TextInput
                label="DATEV-E-Mail-Adresse"
                type="email"
                value={incomingEmail}
                onChange={(e) => setIncomingEmail(e.target.value)}
                placeholder="datev@beispiel.de"
                autoComplete="off"
              />
              <p className="text-xs text-label-secondary">
                Leer lassen, um den Versand nach der Freigabe auszuschalten. Verschickt werden das
                Rechnungs-PDF und, falls vorhanden, die XML-Datei der E-Rechnung.
              </p>
              <SenderAddressField address={fromAddress} />
            </div>
          </div>
        )}
      </Card>

      <Card
        title="SharePoint-Nachzug"
        description="Lädt PDFs erstellter Geschäftsdokumente nachträglich in den Projektordner auf SharePoint – für Dokumente, die dort noch fehlen."
        actions={
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handleBackfill()}
            disabled={backfilling || loading}
          >
            {backfilling ? "Läuft …" : "Nachzug starten"}
          </Button>
        }
      >
        <p className="text-sm text-label-secondary">
          Betrifft Angebote, Rechnungen, Lieferscheine und Auftragsbestätigungen mit Status ab
          „erstellt“, sofern dem Projekt ein SharePoint-Ordner zugeordnet ist.
        </p>
      </Card>
    </div>
  );
}
